import './v-quantity.css'

class VQuantity extends HTMLElement {
  static observedAttributes = ['data-value']

  #input = null
  #decreaseButton = null
  #increaseButton = null
  #max = null
  #min = null
  #value = 0
  #unit = ''
  #multiplier = 2
  #name = null
  #boundHandleDecrease = null
  #boundHandleIncrease = null
  #boundHandleInput = null

  constructor() {
    super()
    this.#boundHandleDecrease = this.#handleDecrease.bind(this)
    this.#boundHandleIncrease = this.#handleIncrease.bind(this)
    this.#boundHandleInput = this.#handleInput.bind(this)
  }

  connectedCallback() {
    this.#max = this.dataset.max ? parseInt(this.dataset.max, 10) : null
    this.#min = this.dataset.min ? parseInt(this.dataset.min, 10) : 0
    this.#unit = this.dataset.unit ?? ''
    this.#multiplier = this.dataset.multiplier ? parseInt(this.dataset.multiplier, 10) : 2
    this.#name = this.dataset.name || null

    const initialValue = parseInt(this.dataset.value || '0', 10)
    this.#value = initialValue

    this.render()
    this.hidden = false

    this.#input = this.querySelector('.v-quantity__input')
    this.#decreaseButton = this.querySelector('.v-quantity__button--decrease')
    this.#increaseButton = this.querySelector('.v-quantity__button--increase')

    if (!this.#input) {
      console.warn('v-quantity: Input element not found')
      return
    }

    this.#input.value = initialValue * this.#multiplier

    if (this.#decreaseButton) {
      this.#decreaseButton.addEventListener('click', this.#boundHandleDecrease)
    }
    if (this.#increaseButton) {
      this.#increaseButton.addEventListener('click', this.#boundHandleIncrease)
    }
    if (this.#input) {
      this.#input.addEventListener('change', this.#boundHandleInput)
    }

    this.#updateButtonStates()
  }

  disconnectedCallback() {
    if (this.#decreaseButton) {
      this.#decreaseButton.removeEventListener('click', this.#boundHandleDecrease)
    }
    if (this.#increaseButton) {
      this.#increaseButton.removeEventListener('click', this.#boundHandleIncrease)
    }
    if (this.#input) {
      this.#input.removeEventListener('change', this.#boundHandleInput)
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-value' && oldValue !== newValue) {
      const quantity = parseInt(newValue || '0', 10)
      this.#value = quantity
      if (this.#input) {
        this.#input.value = quantity * this.#multiplier
      }
      this.#updateButtonStates()
    }
  }

  render() {
    const variantId = this.dataset.variantId || ''
    const displayValue = this.#value * this.#multiplier
    const unitHtml = this.#unit ? `<span class="v-quantity__unit v:text-sm v:font-normal v:text-text-primary v:ml-0.5">${this.#unit}</span>` : ''

    const html = `
      <button type="button"
        class="v-quantity__button v-quantity__button--decrease v:flex v:items-center v:justify-center v:size-6 v:text-text-primary v:bg-transparent v:border-none v:cursor-pointer v:leading-none"
        aria-label="Decrease quantity">
        <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 1H12" stroke="currentColor" stroke-width="1.5"/>
        </svg>
      </button>

      <div class="v-quantity__field v:flex v:flex-row v:items-center v:h-10 v:px-3 v:border v:border-gray-25 v:rounded-sm">
        <input type="number"
          class="v-quantity__input v:w-8 v:text-center v:border-none v:bg-transparent v:text-base v:font-bold v:text-text-primary v:outline-none"
          value="${displayValue}"
          min="${this.#min}"
          ${this.#max !== null ? `max="${this.#max}"` : ''}
          ${this.#name ? `name="${this.#name}"` : ''}
          aria-label="Quantity"
          ${variantId ? `data-variant-id="${variantId}"` : ''}>

        ${unitHtml}
      </div>

      <button type="button"
        class="v-quantity__button v-quantity__button--increase v:flex v:items-center v:justify-center v:size-6 v:text-text-primary v:bg-transparent v:border-none v:cursor-pointer v:leading-none"
        aria-label="Increase quantity">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 0V12M0 6H12" stroke="currentColor" stroke-width="1.5"/>
        </svg>
      </button>
    `

    this.innerHTML = html
  }

  #handleDecrease(event) {
    event.preventDefault()
    if (!this.#input) return

    const newValue = Math.max(this.#value - 1, this.#min)
    this.#updateValue(newValue)
  }

  #handleIncrease(event) {
    event.preventDefault()
    if (!this.#input) return

    const newValue = this.#value + 1

    if (this.#max !== null && newValue > this.#max) {
      return
    }

    this.#updateValue(newValue)
  }

  #handleInput(event) {
    const inputValue = parseInt(this.#input.value || '0', 10)
    const quantity = Math.ceil(inputValue / this.#multiplier)

    let constrainedQuantity = Math.max(quantity, this.#min)
    if (this.#max !== null) {
      constrainedQuantity = Math.min(constrainedQuantity, this.#max)
    }

    const oldQuantity = this.#value
    this.#value = constrainedQuantity
    this.#input.value = constrainedQuantity * this.#multiplier

    this.#updateButtonStates()
    this.#emitChangeEvent(constrainedQuantity, oldQuantity)
  }

  #updateValue(newQuantity, emit = true) {
    if (!this.#input) return

    const oldQuantity = this.#value
    this.#value = newQuantity
    this.#input.value = newQuantity * this.#multiplier

    this.#updateButtonStates()

    if (oldQuantity !== newQuantity && emit) {
      this.#emitChangeEvent(newQuantity, oldQuantity)
      this.dataset.value = newQuantity
    }
  }

  #updateButtonStates() {
    if (!this.#input) return

    if (this.#decreaseButton) {
      this.#decreaseButton.disabled = this.#value <= this.#min
    }

    if (this.#increaseButton && this.#max !== null) {
      this.#increaseButton.disabled = this.#value >= this.#max
    }
  }

  #emitChangeEvent(newQuantity, oldQuantity = 0) {
    const variantId = this.dataset.variantId

    this.dispatchEvent(new CustomEvent('v-quantity:change', {
      detail: {
        value: newQuantity,
        oldValue: oldQuantity,
        variantId
      },
      bubbles: true
    }))
  }
}

if (!customElements.get('v-quantity')) {
  customElements.define('v-quantity', VQuantity)
}

export default VQuantity